import axios, { AxiosError, AxiosInstance } from "axios";
import BaseController from "./BaseController";
import { BlockJsonModel, CategoryJsonModel, CategoryPackageJsonModel, LayoutJsonModel, LicenseJsonModel, ModeJsonModel } from "../models/BlockTransportSystemJsonModel";
import { AdminUserModel } from "../models/AccountPermissionModel";
import i18n from "../../shared/internationalization/I18nSetting";

export interface ExposureCategoryModel {
  modeId: string;
  categoryId: string;
}

export interface ExposureLicenseModel {
  modeId: string;
  licenseId: string;
}

// 계정 권한 설정 — API 응답 래퍼
export interface AdminUsersResponseModel {
  users: AdminUserModel[];
}

export interface UserModesResponseModel {
  modeIds: string[];
  modes: ModeJsonModel[];
}

export interface UserLocalesResponseModel {
  locales: string[];
}

export interface UserCategoriesResponseModel {
  categoryIds: string[];
}

export interface UserLicensesResponseModel {
  licenseIds: string[];
  licenses: LicenseJsonModel[];
}

export interface TemplateResponseModel {
  baseUrl: string;
  templates: TemplateModel[];
}

export interface TemplateModel {
  templateId: string,
  templateType: string,
  name: string,
  size: string,
  direction: string,
  layoutUrl: string,
  previewUrl: string,
  priority: number,
  createDate: Date
}

export interface UserPermissionModel {
  userId: string;
  permissions: string[];
}

export interface UserPermissionResponse<T> {
  permissions: T[];
}

export type PermissionEntityType = "categories" | "licenses" | "blocks" | "category-packages";

export interface ApiResultModel {
  isSuccess: boolean;
  message: string;
}

export default class StudioAdminController extends BaseController {
  private client: AxiosInstance;

  constructor(hostUrl: string, apiKey: string) {
    super(hostUrl, apiKey);
    this.client = axios.create({
      headers: {
        "x-functions-key": this.apiKey,
        "content-type": "application/json"
      },
      withCredentials: true
    });
  }

  isAxiosError(error: unknown): error is AxiosError {
    return (error as AxiosError).response !== undefined;
  }

  getErrorMessage(error: unknown): ApiResultModel {
    const axiosError = error as AxiosError;
      if (axiosError.response?.status === 403) {
        return {
          isSuccess: false,
          message: i18n.t("permission-not-allowed-message")
        };
      } else {
        const status = axiosError.response?.status;
        return {
          isSuccess: false,
          message: status ? `${i18n.t("unknown-error")} (${status})` : i18n.t("unknown-error")
        }
      }
  }

  // 계정 권한 설정 — 유저 목록
  public async getUsersAsync() {
    const response = await this.client.get<AdminUsersResponseModel>(`${this.hostUrl}/api/v1/admin/users`);
    return response.data.users;
  }

  // 계정 권한 설정 — 유저 생성 (email, password, displayName)
  public async postUserAsync(email: string, password: string, displayName: string) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users`,
    {
      email,
      password,
      displayName
    });
    return response.data;
  }

  // 계정 권한 설정 — 유저 수정 (name, role)
  public async putUserAsync(userId: string, name: string | null, role: string, allowedPresets?: string[]) {
    const response = await this.client.put(`${this.hostUrl}/api/v1/admin/users/${userId}`,
    {
      name,
      role,
      allowedPresets
    });
    return response.data;
  }

  // 계정 권한 설정 — 유저에 부여된 모드 id 목록
  public async getAdminUserModesAsync(userId: string) {
    const response = await this.client.get<UserModesResponseModel>(`${this.hostUrl}/api/v1/admin/users/${userId}/modes`);
    return response.data.modeIds;
  }

  public async getUserModesAsync() {
    const response = await this.client.get<UserModesResponseModel>(`${this.hostUrl}/api/v1/user/modes`);
    return response.data.modes;
  }

  // 계정 권한 설정 — 유저에 부여된 로케일 코드 목록
  public async getUserLocalesAsync(userId: string) {
    const response = await this.client.get<UserLocalesResponseModel>(`${this.hostUrl}/api/v1/admin/users/${userId}/locales`);
    return response.data.locales;
  }

  // 계정 권한 설정 — 유저에 로케일 추가
  public async postUserLocalesAsync(userId: string, locales: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/locales`,
    {
      locales
    });
    return response.data;
  }

  // 계정 권한 설정 — 유저에서 로케일 삭제
  public async deleteUserLocalesAsync(userId: string, locales: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/locales/bulk-delete`,
    {
      locales
    });
    return response.data;
  }

  // 계정 권한 설정 — 모드 내 부여된 카테고리 id 목록
  public async getUserModeCategoriesAsync(userId: string, modeId: string) {
    const response = await this.client.get<UserCategoriesResponseModel>(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/categories`);
    return response.data.categoryIds;
  }

  // 계정 권한 설정 — 모드 내 부여된 라이선스 id 목록
  public async getUserModeLicensesAsync(userId: string, modeId: string) {
    const response = await this.client.get<UserLicensesResponseModel>(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/licenses`);
    return response.data.licenseIds;
  }

  public async getUserLicensesAsync() {
    const response = await this.client.get<UserLicensesResponseModel>(`${this.hostUrl}/api/v1/user/licenses`);
    return response.data.licenses;
  }

  // 계정 권한 설정 — 유저에 모드 추가 (isIncludeCategories: 모드 포함 카테고리도 추가할지)
  public async postUserModesAsync(userId: string, modeIds: string[], isIncludeCategories: boolean) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes`,
    {
      modeIds,
      isIncludeCategories
    });
    return response.data;
  }

  // 계정 권한 설정 — 유저에서 모드 삭제 (isIncludeCategories: 모드 포함 카테고리도 삭제할지)
  public async deleteUserModesAsync(userId: string, modeIds: string[], isIncludeCategories: boolean) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/bulk-delete`,
    {
      modeIds,
      isIncludeCategories
    });
    return response.data;
  }

  // 계정 권한 설정 — 모드 내 카테고리 추가
  public async postUserModeCategoriesAsync(userId: string, modeId: string, categoryIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/categories`,
    {
      categoryIds
    });
    return response.data;
  }

  // 계정 권한 설정 — 모드 내 카테고리 삭제
  public async deleteUserModeCategoriesAsync(userId: string, modeId: string, categoryIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/categories/bulk-delete`,
    {
      categoryIds
    });
    return response.data;
  }

  // 계정 권한 설정 — 모드 내 라이선스 추가
  public async postUserModeLicensesAsync(userId: string, modeId: string, licenseIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/licenses`,
    {
      licenseIds
    });
    return response.data;
  }

  // 계정 권한 설정 — 모드 내 라이선스 삭제
  public async deleteUserModeLicensesAsync(userId: string, modeId: string, licenseIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/users/${userId}/modes/${modeId}/licenses/bulk-delete`,
    {
      licenseIds
    });
    return response.data;
  }

  public async getCategoryBlocksAsync(categoryId: string) {
    const response = await this.client.get<BlockJsonModel[]>(`${this.hostUrl}/api/v1/admin/categories/${categoryId}/blocks`,
    {
      params: {
        modelVersion: "1"
      }
    });

    return response.data;
  }

  public async getBlocksStagingAsync(categoryId?: string) {
    const response = await this.client.get<BlockJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/blocks`,
      {
        params: {
          categoryId
        }
      }
    );
    return response.data;
  }

  public async publishBlockStagingAsync(block: BlockJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/categories/${block.categoryId}/blocks/${block.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  // 그리드나 입력창에서 비운 가격은 "" 또는 null로 들어온다. Number()로 바꾸면 0이 되므로 가격 없음은 null로 보낸다.
  private toPriceOrNull(price: unknown): number | null {
    return price === undefined || price === null || price === "" ? null : Number(price);
  }

  public async postCategoryBlockAsync(block: BlockJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/categories/${block.categoryId}/blocks/${block.id}`,
      {
        price: this.toPriceOrNull(block.price),
        prices: block.prices
      });
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async deletePublishedBlockAsync(block: BlockJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete(`${this.hostUrl}/api/v1/admin/staging/categories/${block.categoryId}/blocks/${block.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async deleteBlockStagingAsync(block: BlockJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<ApiResultModel>(`${this.hostUrl}/api/v1/admin/staging/categories/${block.categoryId}/blocks/${block.id}`);
      return response.data;
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async postBlockStagingAsync(block: BlockJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/blocks`, block);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async publishLicenseStagingAsync(license: LicenseJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/licenses/${license.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async postLicenseStagingAsync(license: LicenseJsonModel) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/licenses`, license);
  }

  public async postLicenseModesStagingAsync(licenseId: string, modeIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/licenses/${licenseId}/modes`,
      {
        modeIds: modeIds
      }
    )
  }

  public async getLicenseModesStagingAsync(licenseId: string) {
    const response = await this.client.get<ExposureLicenseModel[]>(`${this.hostUrl}/api/v1/admin/staging/licenses/${licenseId}/modes`);
    return response.data;
  }

  public async getLicensesAsync() {
    const response = await this.client.get<LicenseJsonModel[]>(`${this.hostUrl}/api/v1/admin/licenses`);
    return response.data;
  }

  public async getLicensesStagingAsync() {
    const response = await this.client.get<LicenseJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/licenses`);
    return response.data;
  }

  public async deleteLicenseStagingAsync(license: LicenseJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<ApiResultModel>(`${this.hostUrl}/api/v1/admin/staging/licenses/${license.id}`);
      return response.data;
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async getCategoriesAsync() {
    const response = await this.client.get<CategoryJsonModel[]>(`${this.hostUrl}/api/v1/admin/categories`);
    return response.data;
  }

  public async getCategoriesStagingAsync(isDeleted: boolean = false) {
    const response = await this.client.get<CategoryJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/categories`,
      {
        params: {
          isDeleted: isDeleted
        }
      });
    return response.data;
  }

  public async putCategoryAsync(category: CategoryJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.put(`${this.hostUrl}/api/v1/admin/categories/${category.id}`,
        {
          price: this.toPriceOrNull(category.price)
        });

      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async deletePublishedCategoryAsync(category: CategoryJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete(`${this.hostUrl}/api/v1/admin/staging/categories/${category.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async postCategoriesStagingAsync(category: CategoryJsonModel) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/categories`, category);
  }

  public async putCategoryStagingAsync(category: CategoryJsonModel) {
    const response = await this.client.put(`${this.hostUrl}/api/v1/admin/staging/categories/${category.id}`, category);
  }

  public async postCategoryModesStaging(categoryId: string, modeIds: string[]) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/categories/${categoryId}/modes`,
      {
        modeIds: modeIds
      }
    )
  }

  // 카테고리 게시
  public async publishCategoryStagingAsync(category: CategoryJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/categories/${category.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async deleteCategoryStagingAsync(category: CategoryJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<ApiResultModel>(`${this.hostUrl}/api/v1/admin/staging/categories/${category.id}`);
      return response.data;
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async getCategoryPackagesAsync() {
    const response = await this.client.get<CategoryPackageJsonModel[]>(`${this.hostUrl}/api/v1/admin/category-packages`);
    return response.data;
  }

  public async getCategoryPackagesStagingAsync() {
    const response = await this.client.get<CategoryPackageJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/category-packages`);
    return response.data;
  }

  public async publishCategoryPackagesAsync(categoryPackage: CategoryPackageJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/category-packages/${categoryPackage.id}/publish`);
      return {
        isSuccess: response.status === 201 || response.status === 200,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async postCategoryPackagesStagingAsync(categoryPackage: CategoryPackageJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/category-packages`, categoryPackage);
      return {
        isSuccess: response.status === 201 || response.status === 200,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async deleteCategoryPackagesStagingAsync(categoryPackage: CategoryPackageJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<ApiResultModel>(`${this.hostUrl}/api/v1/admin/staging/category-packages/${categoryPackage.id}`);
      return response.data;
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async getModesAsync() {
    const response = await this.client.get<ModeJsonModel[]>(`${this.hostUrl}/api/v1/admin/modes`);
    return response.data;
  }

  public async getModesStagingAsync() {
    const response = await this.client.get<ModeJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/modes`);
    return response.data;
  }

  public async publishModeStagingAsync(mode: ModeJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/modes/${mode.id}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  // 모드 생성
  public async postModesStagingAsync(mode: ModeJsonModel) {
    const response = await this.client.post<ModeJsonModel>(`${this.hostUrl}/api/v1/admin/staging/modes`,
    {
      name: mode.name,
      description: mode.description
    });

    return response.data;
  }

  // 모드 업데이트
  public async postModeStagingAsync(mode: ModeJsonModel) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/modes/${mode.id}`,
    {
      description: mode.description
    });
  }

  public async getExposureCategoriesAsync() {
    const response = await this.client.get<ExposureCategoryModel[]>(`${this.hostUrl}/api/v1/admin/exposure-categories`);
    return response.data;
  }

  public async getExposureCategoriesStagingAsync(category: CategoryJsonModel) {
    const response = await this.client.get<ExposureCategoryModel[]>(`${this.hostUrl}/api/v1/admin/staging/exposure-categories`,
    {
      params: {
        categoryId: category?.id
      }
    });
    return response.data;
  }

  public async postExposureCategoriesStagingAsync(mode: ModeJsonModel) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/modes/${mode.id}/categories`,
    {
      categoryIds: mode.exposureCategoryIds
    });
  }

  public async postExposureCategoryStagingAsync(modeId: string, categoryId: string) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/modes/${modeId}/categories/${categoryId}`);
  }

  public async postExposureLicensesStagingAsync(mode: ModeJsonModel) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/staging/modes/${mode.id}/licenses`,
    {
      licenseIds: mode.exposureLicenseIds
    });
  }

  public async deleteModeStagingAsync(mode: ModeJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<ApiResultModel>(`${this.hostUrl}/api/v1/admin/staging/modes/${mode.id}`);
      return response.data;
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async getLayoutsStagingAsync() {
    const response = await this.client.get<LayoutJsonModel[]>(`${this.hostUrl}/api/v1/admin/staging/layouts`);
    return response.data;
  }

  public async getLayoutsAsync() {
    const response = await this.client.get<LayoutJsonModel[]>(`${this.hostUrl}/api/v1/admin/layouts`);
    return response.data;
  }

  public async postLayoutsStagingAsync(layout: LayoutJsonModel) {
    const response = await this.client.post<LayoutJsonModel>(`${this.hostUrl}/api/v1/admin/staging/layouts`, layout);
    return response.data;
  }

  public async publishLayoutsStagingAsync(layout: LayoutJsonModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post<LayoutJsonModel>(`${this.hostUrl}/api/v1/admin/staging/layouts/${layout.layoutId}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async putLayoutStagingAsync(layout: LayoutJsonModel) {
    const response = await this.client.put<LayoutJsonModel>(`${this.hostUrl}/api/v1/admin/staging/layouts/${layout.layoutId}`, layout);
    return response.data;
  }

  public async getTemplatesStagingAsync() {
    const response = await this.client.get<TemplateResponseModel>(`${this.hostUrl}/api/v1/admin/staging/templates`);
    return response.data;
  }

  public async getTemplatesAsync() {
    const response = await this.client.get<TemplateResponseModel>(`${this.hostUrl}/api/v1/admin/templates`);
    return response.data;
  }

  public async deleteTemplateStagingAsync(template: TemplateModel) {
    const response = await this.client.delete(`${this.hostUrl}/api/v1/admin/staging/templates/${template.templateId}`);
  }

  public async putTemplateStagingAsync(template: TemplateModel) {
    const response = await this.client.put<TemplateModel>(`${this.hostUrl}/api/v1/admin/staging/templates/${template.templateId}`,
    {
      name: template.name,
      templateType: template.templateType,
      priority: template.priority
    });
    return response.data;
  }

  public async publishTemplateStagingAsync(template: TemplateModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.post<TemplateModel>(`${this.hostUrl}/api/v1/admin/staging/templates/${template.templateId}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  public async unpublishTemplateStagingAsync(template: TemplateModel): Promise<ApiResultModel> {
    try {
      const response = await this.client.delete<TemplateModel>(`${this.hostUrl}/api/v1/admin/staging/templates/${template.templateId}/publish`);
      return {
        isSuccess: true,
        message: response.statusText
      }
    } catch (error: unknown) {
      return this.getErrorMessage(error);
    }
  }

  // 권한 설정
  public async getEntityPermissionsAsync(entityType: PermissionEntityType, entityId: string) {
    const response = await this.client.get<UserPermissionResponse<UserPermissionModel>>(`${this.hostUrl}/api/v1/admin/${entityType}/${entityId}/permissions`);
    return response.data.permissions;
  }

  public async postEntityPermissionAsync(entityType: PermissionEntityType, entityId: string, permissions: UserPermissionModel[], isCascadePermissionToBlocks: boolean = false) {
    const response = await this.client.post(`${this.hostUrl}/api/v1/admin/${entityType}/${entityId}/permissions`,
      {
        permissions,
        isCascadePermissionToBlocks
      }
    );
  };
}